/**
 * Baixar os contatos de VÁRIOS grupos de uma vez — os hooks e o endereço do arquivo.
 *
 * CONTRATO REAL (conferido em `apps/api/src/modules/group/group.router.ts` e
 * `apps/api/src/routes/export-contacts.ts`):
 *
 *   group.workspaceContactsExport { workspaceId, groupIds?, situacao?, admins? }
 *     -> { rows, rowsWithPhone, people, peopleWithPhone, groups, neverSyncedGroups }
 *
 *   GET /export/workspaces/:ws/contacts.csv?grupos=<ids,>&situacao=todos|no_grupo|saiu
 *                                          &admins=incluir|excluir
 *
 * `grupos` vazio (ou `groupIds: null`) = TODOS os grupos do workspace, inclusive os que
 * deixaram de ser monitorados. Por isso "todos marcados" vira lista vazia: o endereço não
 * cresce com 200 ids, e "todos" quer dizer a mesma coisa nos dois lados.
 *
 * Os números da prévia vêm prontos do servidor (invariante 3): a tela não conta linha.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { API_ORIGIN, useTRPC } from '../../lib/api';
import type { ApiClient, QueryOutput } from '../../lib/api';

export type ContatosSituacao = 'todos' | 'no_grupo' | 'saiu';
export type ContatosAdmins = 'incluir' | 'excluir';

export type ContatosFiltro = {
  /** `null` = todos os grupos. Nunca `[]`: nenhum grupo marcado não é pedido válido. */
  groupIds: string[] | null;
  situacao: ContatosSituacao;
  admins: ContatosAdmins;
};

export type WorkspaceContactsPreview = QueryOutput<ApiClient['group']['workspaceContactsExport']>;

/**
 * A seleção da tela, no formato do servidor: todos marcados vira `null` (= todos).
 * Devolve `undefined` quando nada está marcado — não há o que pedir.
 */
export function groupIdsDaSelecao(
  marcados: ReadonlySet<string>,
  todos: readonly { id: string }[],
): string[] | null | undefined {
  const ids = todos.filter((g) => marcados.has(g.id)).map((g) => g.id);
  if (ids.length === 0) return undefined;
  if (ids.length === todos.length) return null;
  return ids;
}

/**
 * TODOS os grupos do workspace, inclusive os não monitorados — a mesma lista que "todos"
 * quer dizer no servidor. Usar a lista da tela (que esconde os não monitorados) faria o
 * contador dizer "30 de 30" enquanto o arquivo leva 32.
 */
export function useGruposParaContatos(workspaceId: string, enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.group.list.queryOptions({ workspaceId, includeArchived: true }),
    enabled,
  });
}

export function useWorkspaceContactsPreview(
  workspaceId: string,
  filtro: ContatosFiltro | undefined,
  enabled: boolean,
) {
  const trpc = useTRPC();
  const input = {
    workspaceId,
    groupIds: filtro?.groupIds ?? null,
    situacao: filtro?.situacao ?? 'todos',
    admins: filtro?.admins ?? 'incluir',
  } as const;
  return useQuery({
    ...trpc.group.workspaceContactsExport.queryOptions(input),
    /**
     * Desligada sem permissão (nem a contagem de telefones é de quem não pode baixar) e
     * sem grupo marcado (lista vazia seria lida como "todos").
     */
    enabled: enabled && filtro !== undefined,
    // Ao trocar um filtro, os números velhos ficam até os novos chegarem — sem piscar.
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
}

/**
 * O endereço do arquivo. Link da MESMA origem: quem autentica é o cookie de sessão, e o
 * navegador salva com o nome que o servidor mandou.
 */
export function workspaceContactsExportUrl(workspaceId: string, filtro: ContatosFiltro): string {
  const params = new URLSearchParams({
    grupos: filtro.groupIds === null ? '' : filtro.groupIds.join(','),
    situacao: filtro.situacao,
    admins: filtro.admins,
  });
  return `${API_ORIGIN}/export/workspaces/${workspaceId}/contacts.csv?${params.toString()}`;
}

export type ContactsExportHistory = QueryOutput<ApiClient['group']['contactsExportHistory']>;

/** Os últimos arquivos de contatos baixados no workspace (quem, quando, o quê). */
export function useContactsExportHistory(workspaceId: string, enabled: boolean) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.group.contactsExportHistory.queryOptions({ workspaceId }),
    enabled,
  });
}
